'use client'

import { useCallback, useMemo, useRef } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useRouter, useSearchParams } from 'next/navigation'
import { useAdminAuth } from '@/Auth/useAdminAuth'
import { useAuth } from '@/Auth/useAuth'
import {
  useDiscoverieSearch,
  type DiscoverieSearchCompletionInfo,
} from '@/DiscoverieSearch/useDiscoverieSearch'
import { usePurchasedProductIdsForDiscoverie } from '@/DiscoverieSearch/usePurchasedProductIdsForDiscoverie'
import { InfoAlert } from '@/General/InfoAlert'
import { LoadingSpinner } from '@/General/LoadingSpinner'
import {
  buildDiscoverieScopedQuery,
  parseDiscoverieScopedQuery,
} from '@/lib/discoverie/querySyntax'
import { canShowDiscoverieAdminViewer } from '@/lib/discoverie/adminViewer'
import { Pagination } from '@/General/Pagination'
import TypesenseClient from '@/search/Typesense'
import { useStore } from '@/store'
import { tracking } from '@/tracking'
import { NoResults } from '../NoResults'
import { SearchResult } from '../SearchResult'

const DISCOVERIE_SORT_OPTIONS = [
  {
    label: 'Best match',
    value: 'best-match',
    sortBy: TypesenseClient.SortBy.DISCOVERIE_BEST_MATCH,
  },
  {
    label: 'Title',
    value: 'title',
    sortBy: TypesenseClient.SortBy.DISCOVERIE_TITLE,
  },
  {
    label: 'Author',
    value: 'author',
    sortBy: TypesenseClient.SortBy.DISCOVERIE_AUTHOR,
  },
  {
    label: 'Newest indexed',
    value: 'newest',
    sortBy: TypesenseClient.SortBy.DISCOVERIE_NEWEST,
  },
] as const

export const SearchResultsList = ({
  hasRecoverableAdminSession,
}: SearchResultsListProps) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const query = searchParams.get('query')
  const page = searchParams.get('page')
  const sort = searchParams.get('sort') || 'best-match'
  const discoverieProductId = searchParams.get('book') || undefined
  const purchasedOnly = searchParams.get('purchased') === 'true'
  const { isAuthenticated } = useAuth()
  const {
    isAuthenticated: isAdminAuthenticated,
    isResolved: isAdminAuthResolved,
  } = useAdminAuth({
    verifyWithoutSessionMarker: hasRecoverableAdminSession,
  })
  const showAdminViewer = canShowDiscoverieAdminViewer({
    hasRecoverableAdminSession,
    isAdminAuthResolved,
    isAdminAuthenticated,
  })
  const customerId = useStore(s => s.user?.id)
  const parsedQuery = useMemo(
    () => parseDiscoverieScopedQuery(query || ''),
    [query]
  )
  const activeSort =
    DISCOVERIE_SORT_OPTIONS.find(option => option.value === sort) ||
    DISCOVERIE_SORT_OPTIONS[0]

  const purchasedOnlyEffective = purchasedOnly && isAuthenticated

  const { data: purchasedIdList, isLoading: isLoadingPurchasedIds } =
    usePurchasedProductIdsForDiscoverie(purchasedOnlyEffective, customerId)

  const purchasedIdsReady = !purchasedOnlyEffective || !isLoadingPurchasedIds
  const purchasedLibraryEmpty =
    purchasedOnlyEffective &&
    purchasedIdsReady &&
    (purchasedIdList?.length ?? 0) === 0

  const purchasedProductIdsCsv = useMemo(() => {
    if (
      !purchasedOnlyEffective ||
      !purchasedIdList ||
      purchasedIdList.length === 0
    ) {
      return undefined
    }
    return purchasedIdList.join(',')
  }, [purchasedOnlyEffective, purchasedIdList])

  // De-dupe Kissmetrics tracking: the hook may fire onSearchComplete twice for the same
  // params under React Strict Mode (effects run setup-cleanup-setup in dev). Skip repeats
  // of the exact same search.
  const lastTrackedKeyRef = useRef<string | null>(null)
  const handleSearchComplete = useCallback(
    (info: DiscoverieSearchCompletionInfo) => {
      const trackedSort = info.sortBy ?? 'best-match'
      const trackedKey = JSON.stringify({
        q: info.query,
        p: info.page,
        s: trackedSort,
        book: info.discoverieProductId ?? '',
        purchased: !!info.purchasedProductIds || info.purchasedLibraryEmpty,
        total: info.totalCount,
      })
      if (lastTrackedKeyRef.current === trackedKey) return
      lastTrackedKeyRef.current = trackedKey

      const parsed = parseDiscoverieScopedQuery(info.query)
      const purchasedOnlySearch =
        !!info.purchasedProductIds || info.purchasedLibraryEmpty

      tracking.track('Searched Discoverie', {
        'Search query': info.query,
        'Search term': parsed.baseQuery || undefined,
        'Title filter': parsed.titleFilter,
        'Author filter': parsed.authorFilter,
        'Number of results': info.totalCount,
        'Has results': info.totalCount > 0,
        'Results page': info.page,
        Sort: trackedSort,
        'Purchased only': purchasedOnlySearch,
        'Scoped to book': info.discoverieProductId,
      })
    },
    []
  )

  const {
    results,
    isLoading: isSearching,
    totalCount,
    error,
  } = useDiscoverieSearch({
    query: query || '',
    sortBy: activeSort.sortBy,
    pagination: { page: Number(page) || 1, limit: 25 },
    purchasedProductIds: purchasedProductIdsCsv,
    discoverieProductId,
    purchasedLibraryEmpty,
    enabled: !!query && purchasedIdsReady,
    onSearchComplete: handleSearchComplete,
  })

  const isLoading = isLoadingPurchasedIds || isSearching
  const loadingMessage = isLoadingPurchasedIds
    ? 'Loading your library…'
    : 'Searching…'

  const updateSearchParams = (updater: (params: URLSearchParams) => void) => {
    const params = new URLSearchParams(searchParams.toString())
    updater(params)
    router.replace(`/discoverie?${params.toString()}`)
  }

  const removeScopedFilter = (field: 'title' | 'author') => {
    const nextQuery = buildDiscoverieScopedQuery({
      baseQuery: parsedQuery.baseQuery,
      titleFilter: field === 'title' ? undefined : parsedQuery.titleFilter,
      authorFilter: field === 'author' ? undefined : parsedQuery.authorFilter,
    })

    updateSearchParams(params => {
      params.delete('book')
      params.set('page', '1')
      if (nextQuery) {
        params.set('query', nextQuery)
      } else {
        params.delete('query')
      }
    })
  }

  const noResultSuggestions = useMemo(() => {
    const suggestions: { href: string; label: string }[] = []

    const buildHref = (nextQuery: string) => {
      const params = new URLSearchParams(searchParams.toString())
      params.delete('book')
      params.set('page', '1')

      if (nextQuery) {
        params.set('query', nextQuery)
      } else {
        params.delete('query')
      }

      return `/discoverie?${params.toString()}`
    }

    if (parsedQuery.titleFilter) {
      suggestions.push({
        href: buildHref(
          buildDiscoverieScopedQuery({
            baseQuery: parsedQuery.baseQuery,
            authorFilter: parsedQuery.authorFilter,
          })
        ),
        label: 'Remove title filter',
      })
    }

    if (parsedQuery.authorFilter) {
      suggestions.push({
        href: buildHref(
          buildDiscoverieScopedQuery({
            baseQuery: parsedQuery.baseQuery,
            titleFilter: parsedQuery.titleFilter,
          })
        ),
        label: 'Remove author filter',
      })
    }

    if (
      parsedQuery.baseQuery &&
      (parsedQuery.titleFilter || parsedQuery.authorFilter)
    ) {
      suggestions.push({
        href: buildHref(parsedQuery.baseQuery),
        label: `Search just for "${parsedQuery.baseQuery}"`,
      })
    }

    return suggestions
  }, [parsedQuery, searchParams])

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="flex flex-col justify-center items-center py-12">
          <LoadingSpinner />
          <p className="mt-3 text-gray-600">{loadingMessage}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden p-6"
        role="alert"
        aria-live="assertive"
      >
        <InfoAlert variant="error" title="Search couldn’t complete">
          <p className="mb-0">
            {error.message ||
              'Something went wrong while searching. Please try again in a moment.'}
          </p>
        </InfoAlert>
      </div>
    )
  }

  if (purchasedOnlyEffective && results.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden p-8 text-center">
        <p className="text-gray-900 font-medium">No matches in books you own</p>
        <p className="mt-2 text-sm text-gray-600">
          Try a broader search, or turn off &quot;Show results from my purchases
          only&quot; to search the full catalogue.
        </p>
      </div>
    )
  }

  if (query && results.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
          <DiscoverieResultsToolbar
            sort={activeSort.value}
            onSortChange={nextSort =>
              updateSearchParams(params => {
                params.set('sort', nextSort)
                params.set('page', '1')
              })
            }
          />
        </div>
        <NoResults suggestions={noResultSuggestions} />
      </div>
    )
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
          <DiscoverieResultsToolbar
            sort={activeSort.value}
            onSortChange={nextSort =>
              updateSearchParams(params => {
                params.set('sort', nextSort)
                params.set('page', '1')
              })
            }
          />
          {(parsedQuery.titleFilter || parsedQuery.authorFilter) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {parsedQuery.titleFilter ? (
                <button
                  type="button"
                  onClick={() => removeScopedFilter('title')}
                  className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-vi-red"
                >
                  title: {parsedQuery.titleFilter}
                  <XMarkIcon className="ml-1 size-3.5" aria-hidden="true" />
                </button>
              ) : null}
              {parsedQuery.authorFilter ? (
                <button
                  type="button"
                  onClick={() => removeScopedFilter('author')}
                  className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-vi-red"
                >
                  author: {parsedQuery.authorFilter}
                  <XMarkIcon className="ml-1 size-3.5" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          )}
        </div>
        <div className="divide-y divide-gray-200">
          {results.map((result, index) => (
            <SearchResult
              key={result.objectId}
              objectId={result.objectId}
              downloadFileId={result.downloadFileId}
              downloadFileName={result.downloadFileName}
              title={result.title}
              author={result.author}
              hasPurchased={result.hasPurchased}
              productUrl={result.productUrl}
              productThumbnail={result.productThumbnail}
              pages={result.pages}
              searchQuery={query || undefined}
              resultPosition={index + 1}
              resultsPage={Number(page) || 1}
              isAdmin={showAdminViewer}
            />
          ))}
        </div>
      </div>
      <div className="mt-6">
        <Pagination
          page={Number(page) || 1}
          perPage={25}
          totalCount={totalCount}
          linkGenerator={page => {
            const params = new URLSearchParams(searchParams.toString())
            params.set('page', page.toString())
            return `/discoverie?${params.toString()}`
          }}
        />
      </div>
    </>
  )
}

interface SearchResultsListProps {
  hasRecoverableAdminSession: boolean
}

const DiscoverieResultsToolbar = ({
  sort,
  onSortChange,
}: {
  sort: string
  onSortChange: (value: string) => void
}) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <p className="text-sm font-semibold text-gray-900">Search results</p>
      <p className="text-xs text-gray-500">
        Use search syntax to narrow by title or author.
      </p>
    </div>
    <label className="flex items-center gap-2 text-sm text-gray-600">
      <span>Sort by</span>
      <select
        value={sort}
        onChange={event => onSortChange(event.target.value)}
        className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-vi-red focus:outline-none focus:ring-2 focus:ring-red-100"
      >
        {DISCOVERIE_SORT_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  </div>
)
